import axios from 'axios';

// Create axios instance with optimized settings
const instance = axios.create({
  baseURL: 'https://www.homecureexpress.com/api',
  timeout: 15000, // Reduced from 30s to 15s for faster error feedback
  headers: {
    'Content-Type': 'application/json'
    // Note: Accept-Encoding is automatically handled by the browser
  }
});

// Simple in-memory cache with TTL
const cache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Generate cache key from request config
const getCacheKey = (config) => {
  return `${config.method}-${config.url}-${JSON.stringify(config.params || {})}`;
};

// Check if cache is valid
const isCacheValid = (cacheEntry) => {
  return cacheEntry && (Date.now() - cacheEntry.timestamp) < CACHE_TTL;
};

// Request interceptor
instance.interceptors.request.use(
  (config) => {
    // Add auth token
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Check cache for GET requests only
    if (config.method === 'get' && !config.skipCache) {
      const cacheKey = getCacheKey(config);
      const cachedResponse = cache.get(cacheKey);

      if (isCacheValid(cachedResponse)) {
        // Return cached response
        config.adapter = () => {
          return Promise.resolve({
            data: cachedResponse.data,
            status: 200,
            statusText: 'OK (Cached)',
            headers: cachedResponse.headers,
            config: config,
            request: {}
          });
        };
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
instance.interceptors.response.use(
  (response) => {
    // Cache successful GET responses
    if (response.config.method === 'get' && !response.config.skipCache) {
      const cacheKey = getCacheKey(response.config);
      cache.set(cacheKey, {
        data: response.data,
        headers: response.headers,
        timestamp: Date.now()
      });

      // Clean up old cache entries (prevent memory leak)
      if (cache.size > 100) {
        const firstKey = cache.keys().next().value;
        cache.delete(firstKey);
      }
    }

    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Utility function to clear cache
export const clearCache = () => {
  cache.clear();
};

// Utility function to clear specific cache entry
export const clearCacheByUrl = (url) => {
  for (const [key] of cache.entries()) {
    if (key.includes(url)) {
      cache.delete(key);
    }
  }
};

export default instance;
